import 'swiper/css'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import { reactMount } from '@/lib/reactMount'
import { useRef, useState, useEffect } from 'react'
import { ArrowRight } from '@/components/Icons'
import { Button } from '@/components/Button'
import { rteBlock as RteBlock } from './rteBlock'
import { loanCalculatorBlock as CalculatorBlock } from './loanCalculator'
import { getImageByBreakpoint } from '@/lib/media'

const BackgroundMedia = ({ media, onVideoEnd, isActive }) => {
  const videoRef = useRef(null)
  const imageByBreakpoint = getImageByBreakpoint(media.Image)
  
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (isActive) {
      // Reset video to beginning and play when slide becomes active
      video.currentTime = 0
      video.play().catch(err => console.log('Video play failed:', err))
    } else {
      // Pause video when slide becomes inactive
      video.pause()
    }
  }, [isActive])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !isActive) return

    const handleEnded = () => {
      onVideoEnd?.()
    }

    const handlePlay = () => {
      // Pause autoplay when video starts playing
      onVideoEnd?.('pause')
    }

    video.addEventListener('ended', handleEnded)
    video.addEventListener('play', handlePlay)

    return () => {
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('play', handlePlay)
    }
  }, [isActive, onVideoEnd])

  if (media?.VideoUrl) {
    return (
      <video
        ref={videoRef}
        src={media.VideoUrl}
        className='w-full h-full object-cover'
        loop={false} // Remove loop so video can end
        muted
        playsInline
      />
    )
  } else if (media?.Image?.Url) {
    return (
      <picture>
        {Object.keys(imageByBreakpoint).reverse().map((minWidth) => (
          <source key={minWidth} media={`(min-width: ${minWidth})`} srcSet={imageByBreakpoint[minWidth]} />
        ))}
        <img
          src={media.Image.Url}
          alt={media.Image.AltText || ''}
          className='w-full h-full object-cover'
        />
      </picture>
    )
  }
  return null
}

const SlideContent = ({ slide, alignment }) => {
  if (!slide) return null
  return (
    <div>
      <div className='line-clamp-6'>
        <h2 className='text-3xl md:text-6xl font-extrabold mb-2 tracking-tight pb-[11px]'>{slide.Title}</h2>
        <p className='text-2xl font-normal mb-3'>{slide.SubTitle}</p>
        {/* Summary is HTML */}
        <div className='text-base font-semibold mb-4' dangerouslySetInnerHTML={{ __html: slide.Summary || '' }} />
      </div>
      <div className={`flex items-center flex-wrap gap-y-2.5 gap-x-6 pointer-events-auto pt-3 lg:pt-5 ${alignment === 'center' ? 'justify-center' : ''}`}>
        {slide.Button?.Url && slide.Button?.Name && (
          <Button
            href={slide.Button.Url}
            target={slide.Button.Target}
            variant='primary'
            size='lg'
          >
            {slide.Button.Name}
          </Button>
        )}
        {slide.ButtonText?.Url && slide.ButtonText?.Name && (
          <Button
            href={slide.ButtonText.Url}
            target={slide.Button.Target}
            variant='link'
            size='lg'
          >
            {slide.ButtonText.Name}
          </Button>
        )}
      </div>
    </div>
  )
}

const NavigationControls = ({ goPrev, goNext, currentSlide, slidesLength, alignment }) => (
  <div className='flex items-end pt-4 lg:pt-10'>
    <div className={`flex w-full items-center gap-4 ${alignment === 'center' ? 'w-[230px] mx-auto' : ''}`}>
      <button
        onClick={goPrev}
        className={`text-primary-50 border border-primary-50 px-5 py-[7px] rounded-full transition-all pointer-events-auto shadow-sm hover:bg-default-100 hover:text-default-900 duration-300 ${alignment === 'center' ? 'order-1' : 'order-2'}`}
      >
        <ArrowRight className='transform rotate-180' />
        <span className='sr-only'>Prev</span>
      </button>
      <span className={`text-primary-50 text-sm px-3 py-1 rounded pointer-events-auto w-16 grow ${alignment === 'center' ? 'order-2 text-center' : 'order-1'}`}>
        {currentSlide + 1} of {slidesLength}
      </span>
      <button
        onClick={goNext}
        className='text-primary-50 border border-primary-50 px-5 py-[7px] rounded-full transition-all pointer-events-auto shadow-sm order-3 hover:bg-default-100 hover:text-default-900 duration-300'
      >
        <ArrowRight />
        <span className='sr-only'>Next</span>
      </button>
    </div>
  </div>
)

const Overlay = () => (
  <div className='absolute inset-0 z-20 pointer-events-none'>
    <div className='_hero-g-l-r absolute inset-0 opacity-60' />
    <div className='_hero-g-b-t absolute inset-0' />
  </div>
)

export const heroCarouselBlock = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current
  const theme = umbraco.Theme || 'dark'
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isFading, setIsFading] = useState(false)
  const [autoplayPaused, setAutoplayPaused] = useState(false)
  const swiperRef = useRef(null)

  const slides = umbraco.Carousel || []

  const handleSlideChange = (swiper) => {
    setCurrentSlide(swiper.realIndex)
    // Resume autoplay when changing slides (in case it was paused by video)
    if (autoplayPaused && swiperRef.current?.swiper?.autoplay) {
      swiperRef.current.swiper.autoplay.start()
      setAutoplayPaused(false)
    }
  }

  const handleVideoEvent = (action) => {
    if (!swiperRef.current?.swiper?.autoplay) return
    if (action === 'pause') {
      // Pause autoplay when video starts playing
      swiperRef.current.swiper.autoplay.stop()
      setAutoplayPaused(true)
    } else {
      // Video ended - fade to next slide and resume autoplay
      setAutoplayPaused(false)
      handleFadeAndSlide(() => {
        swiperRef.current.swiper.slideNext()
      })
      // Resume autoplay after fade completes
      setTimeout(() => {
        if (swiperRef.current?.swiper?.autoplay) {
          swiperRef.current.swiper.autoplay.start()
        }
      }, FADE_DURATION + 50)
    }
  }

  const FADE_DURATION = 300 // ms, match with CSS

  const handleFadeAndSlide = (slideFn) => {
    setIsFading(true)
    setTimeout(() => {
      slideFn()
      setTimeout(() => setIsFading(false), 10) // allow React to re-render
    }, FADE_DURATION)
  }

  const goNext = () => {
    handleFadeAndSlide(() => {
      if (swiperRef.current?.swiper) {
        swiperRef.current.swiper.slideNext()
      }
    })
  }

  const goPrev = () => {
    handleFadeAndSlide(() => {
      if (swiperRef.current?.swiper) {
        swiperRef.current.swiper.slidePrev()
      }
    })
  }

  const alignment = umbraco.Alignment || 'left'
  const blocks = umbraco.Blocks || []
  const bottomContentImageCta = umbraco.BottomContentImageCta || null

  return (
    <div data-theme={theme}>
      <div className='relative bg-primary-950'>
        {/* Background Swiper */}
        <Swiper
          ref={swiperRef}
          spaceBetween={0}
          slidesPerView={1}
          loop={slides.length >= 3}
          pagination={{ clickable: true }}
          scrollbar={{ draggable: true }}
          autoplay={{
            delay: 6000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true
          }}
          modules={[Autoplay]}
          onSlideChange={handleSlideChange}
          className='h-[80vh] min-h-[740px] xl:min-h-[760px]'
        >
          {slides.map((slide, idx) => (
            <SwiperSlide key={idx}>
              <div className='absolute inset-0'>
                <BackgroundMedia 
                  media={slide.Media} 
                  onVideoEnd={handleVideoEvent}
                  isActive={idx === currentSlide}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Content overlay positioned on top of Swiper */}
        <div className='absolute inset-0 z-30 flex items-end pointer-events-none'>
          <div className='mx-auto w-full max-w-[1440px] h-full flex items-end px-4 md:px-6 lg:px-8'>
            <div className={`text-white w-full pointer-events-none flex flex-col justify-end h-full ${(blocks.length === 0 || !bottomContentImageCta) ? 'pb-8 md:pb-16 xl:pb-[150px]' : 'pb-10 xl:pb-[150px]'} ${alignment === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-[480px]'} `}>
              <div className={`transition-opacity duration-300 ${isFading ? 'opacity-0' : 'opacity-100'}`}>
                <SlideContent slide={slides[currentSlide]} alignment={alignment} />
              </div>

              {slides.length > 1 && (
                <NavigationControls
                  goPrev={goPrev}
                  goNext={goNext}
                  currentSlide={currentSlide}
                  slidesLength={slides.length}
                  alignment={alignment}
                />
              )}
            </div>
          </div>
        </div>

        <Overlay />
      </div>

      <div className='bg-primary-950'>
        {umbraco.BottomContentImage && (
          <div className='relative z-30 px-4 max-w-[722px] mx-auto last:pb-8 -mt-6 xl:-mt-28'>

            {umbraco.BottomContentImageCta?.Url
              ? (
                <a href={umbraco.BottomContentImageCta.Url} target={umbraco.BottomContentImageCta.Target || '_self'} className='relative w-full aspect-contentHeroBottom block' title={umbraco.BottomContentImage?.AltText || ''}>
                  <picture>
                    <source srcSet={umbraco.BottomContentImage?.Url640} media='(min-width: 640px)' />
                    <img src={umbraco.BottomContentImage?.Url} alt={umbraco.BottomContentImage?.AltText || ''} className='absolute inset-0 w-full h-full object-cover' />
                  </picture>
                </a>
                )
              : (
                <div className='relative w-full aspect-contentHeroBottom'>
                  <img src={umbraco.BottomContentImage?.Url} alt={umbraco.BottomContentImage?.AltText || ''} className='absolute inset-0 w-full h-full object-cover' />
                </div>
                )}
          </div>
        )}
        {umbraco.Blocks?.map((block, index) => {
          if (block.BlockType === 'rteBlock') {
            return <RteBlock version='hero-carousel' key={'RteBlock-' + index} umbraco={block} />
          }
          if (block.BlockType === 'calculatorBlock') {
            return <CalculatorBlock version='hero-carousel' key={'CalculatorBlock-' + index} umbraco={block} />
          }
          return null
        })}

      </div>
    </div>
  )
}

reactMount('heroCarouselBlock', heroCarouselBlock)
